"use client";
import emailjs from "emailjs-com";

function getBasicClientEnv() {
  const env = {
    serviceId: process.env.NEXT_PUBLIC_EMAILJS_CRASH_SERVICE_ID,
    templateId: process.env.NEXT_PUBLIC_EMAILJS_CRASH_TEMPLATE_ID,
    publicKey: process.env.NEXT_PUBLIC_EMAILJS_CRASH_PUBLIC_KEY,
    toEmail: process.env.NEXT_PUBLIC_ERROR_REPORT_TO,
  };
  return env;
}

function serializeError(error) {
  if (!error) return "Unknown error";

  if (error instanceof Error) {
    try {
      return JSON.stringify(
        {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        null,
        2
      );
    } catch {
      return String(error);
    }
  }

  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

function formatResolvedFramesToText(frames) {
  if (!Array.isArray(frames) || frames.length === 0) return "";
  try {
    return frames
      .map((f) => {
        const fn = f.functionName || "anonymous";
        const file = f.fileName || "unknown";
        const line = typeof f.lineNumber === "number" ? f.lineNumber : "?";
        const col = typeof f.columnNumber === "number" ? f.columnNumber : "?";
        return `at ${fn} (${file}:${line}:${col})`;
      })
      .join("\n");
  } catch {
    return "";
  }
}

const reportedErrorKeys = new Set();

export function getErrorDedupKey(error, context = {}) {
  const name = error?.name || "Error";
  const message = error?.message || "";
  let file = "";
  let line = "";
  if (context.resolved_frames && context.resolved_frames.length > 0) {
    file = context.resolved_frames[0]?.fileName || "";
    line = context.resolved_frames[0]?.lineNumber || "";
  } else if (error?.stack) {
    // fallback: try to parse stack
    const match = error.stack.match(/\(([^:]+):(\d+):\d+\)/);
    if (match) {
      file = match[1];
      line = match[2];
    }
  }
  return `${name}:${message}:${file}:${line}`;
}

export function isErrorAlreadyReported(key) {
  return reportedErrorKeys.has(key);
}

export function markErrorAsReported(key) {
  reportedErrorKeys.add(key);
  setTimeout(() => reportedErrorKeys.delete(key), 5000); // allow re-report after 5s
}

export async function reportClientError(error, context = {}) {
  const {
    serviceId,
    templateId,
    publicKey,
    toEmail: defaultToEmail,
  } = getBasicClientEnv();
  const resolvedToEmail = context.toEmail || defaultToEmail;

  if (!serviceId || !templateId || !publicKey || !resolvedToEmail) {
    console.warn("[EmailJS] EmailJS error reporting is not fully configured.");
    return;
  }

  const resolvedFrames = Array.isArray(context.resolved_frames)
    ? context.resolved_frames
    : [];
  const firstFrame =
    resolvedFrames && resolvedFrames.length > 0 ? resolvedFrames[0] : undefined;

  const basePayload = {
    to_email: resolvedToEmail,
    subject: context.subject || "Client Error Report",
    error_name: error?.name || "Error",
    error_message: context.message || error?.message || "Unknown error",
    error_source: context.error_source || "unknown",
    error_stack: serializeError(error),
    layout: context.layout || "unknown",
    app_route:
      context.route ||
      (typeof window !== "undefined" ? window.location?.href : "n/a"),
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "n/a",
    timestamp_iso: new Date().toISOString(),
    additional_context: JSON.stringify(context, null, 2),
    // Resolved stack frames from stacktrace-js
    resolved_frames_json: JSON.stringify(resolvedFrames, null, 2),
    resolved_frames_text: formatResolvedFramesToText(resolvedFrames),
    top_frame_file: firstFrame?.fileName || "",
    top_frame_function: firstFrame?.functionName || "",
    top_frame_line:
      typeof firstFrame?.lineNumber === "number"
        ? String(firstFrame.lineNumber)
        : "",
    top_frame_column:
      typeof firstFrame?.columnNumber === "number"
        ? String(firstFrame.columnNumber)
        : "",
  };

  try {
    const recipients = String(resolvedToEmail)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (recipients.length <= 1) {
      await emailjs.send(serviceId, templateId, basePayload, publicKey);
    } else {
      for (const recipient of recipients) {
        await emailjs.send(
          serviceId,
          templateId,
          { ...basePayload, to_email: recipient },
          publicKey
        );
      }
    }
  } catch (sendErr) {
    console.error("[EmailJS] Failed to send error email via EmailJS:", sendErr);
  }
}
